import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/apiClient";
import { toast } from "sonner";
import { Loader2, Mail, Link2, Copy, Trash2, Send } from "lucide-react";

const STATUS_META = {
  PENDING: ["En attente", "text-amber-400 bg-amber-500/15 border-amber-500/30"],
  ACCEPTED: ["Acceptée", "text-emerald-400 bg-emerald-500/15 border-emerald-500/30"],
  DECLINED: ["Refusée", "text-red-400 bg-red-500/15 border-red-500/30"],
  REMOVED: ["Annulée", "text-zinc-500 bg-zinc-800 border-zinc-700"],
};

export default function ManagerInvitations() {
  const { identity } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [invs, setInvs] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (identity && !identity.roles?.includes("MANAGER")) navigate("/become-manager"); }, [identity, navigate]);

  const load = useCallback(() => api.get("/manager/invitations").then((r) => setInvs(r.data.invitations)).catch(() => setInvs([])), []);
  useEffect(() => { load(); }, [load]);

  const invite = async (withEmail) => {
    setBusy(true);
    try {
      const { data } = await api.post("/manager/invitations", withEmail ? { email } : {});
      if (withEmail) setEmail("");
      await navigator.clipboard?.writeText(data.link).catch(() => {});
      toast.success(withEmail ? "Invitation envoyée — lien copié" : "Lien d'invitation généré et copié");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de créer l'invitation.");
    } finally { setBusy(false); }
  };

  const copy = (link) => { navigator.clipboard?.writeText(link); toast.success("Lien copié"); };
  const revoke = async (id) => { await api.delete(`/manager/invitations/${id}`); load(); };

  return (
    <div className="space-y-6" data-testid="manager-invitations">
      <div>
        <h1 className="font-display text-3xl font-extrabold">Invitations</h1>
        <p className="text-zinc-500 text-sm mt-1">Invite un membre par email ou via un lien. Il devra accepter pour être relié.</p>
      </div>

      <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-5 space-y-3" data-testid="invite-form">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Mail className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="email@artiste.com" data-testid="invite-email-input"
              className="w-full h-11 pl-10 pr-4 rounded-xl bg-[#0F0F13] border border-zinc-800 text-sm text-zinc-200 focus:border-amber-500/50 outline-none" />
          </div>
          <button onClick={() => invite(true)} disabled={busy || !email.trim()} data-testid="invite-email-btn"
            className="h-11 px-5 rounded-xl gold-gradient text-black font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Inviter
          </button>
        </div>
        <button onClick={() => invite(false)} disabled={busy} data-testid="invite-link-btn"
          className="w-full h-11 rounded-xl bg-zinc-900 border border-zinc-800 hover:border-amber-500/50 text-sm text-zinc-200 inline-flex items-center justify-center gap-2">
          <Link2 className="w-4 h-4 text-amber-400" /> Générer un lien d'invitation
        </button>
      </div>

      {invs === null ? (
        <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 text-amber-500 animate-spin" /></div>
      ) : invs.length === 0 ? (
        <div className="text-center py-12 text-zinc-600 text-sm" data-testid="invites-empty">Aucune invitation envoyée.</div>
      ) : (
        <div className="space-y-2" data-testid="invites-list">
          {invs.map((i) => {
            const [label, cls] = STATUS_META[i.status] || ["", ""];
            return (
              <div key={i.id} className="flex items-center gap-3 bg-[#18181E] border border-zinc-800 rounded-2xl p-3" data-testid={`invite-${i.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-zinc-200 truncate">{i.email || "Lien d'invitation"}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${cls}`}>{label}</span>
                  </div>
                  <p className="text-[11px] text-zinc-600 truncate mt-0.5">{i.link}</p>
                </div>
                {i.status === "PENDING" && (
                  <>
                    <button onClick={() => copy(i.link)} data-testid={`invite-copy-${i.id}`} className="text-zinc-400 hover:text-amber-400 shrink-0"><Copy className="w-4 h-4" /></button>
                    <button onClick={() => revoke(i.id)} data-testid={`invite-revoke-${i.id}`} className="text-zinc-500 hover:text-red-400 shrink-0"><Trash2 className="w-4 h-4" /></button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
